import { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_END_THRESHOLD_PX = 4;
const SCROLL_DEBOUNCE_MS = 250;

interface UseScrollHintOptions {
  axis: "horizontal" | "vertical";
  debounceMs?: number;
}

export function useScrollHint<T extends HTMLElement>({
  axis,
  debounceMs = SCROLL_DEBOUNCE_MS,
}: UseScrollHintOptions) {
  const ref = useRef<T>(null);
  const [hasMore, setHasMore] = useState(axis === "horizontal");
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (axis === "horizontal") {
      setHasMore(
        el.scrollLeft + el.clientWidth <
          el.scrollWidth - SCROLL_END_THRESHOLD_PX,
      );
    } else {
      setHasMore(
        el.scrollTop + el.clientHeight <
          el.scrollHeight - SCROLL_END_THRESHOLD_PX,
      );
    }
  }, [axis]);

  const onScroll = useCallback(() => {
    if (debounceMs > 0) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(checkScroll, debounceMs);
    } else {
      checkScroll();
    }
  }, [checkScroll, debounceMs]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return { ref, hasMore, onScroll, checkScroll };
}
